import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../shared/api/errors';
import { Button } from '../../../shared/ui/button';
import { CodeView } from '../../../shared/ui/code-view';
import { FieldLabel } from '../../../shared/ui/field-label';
import { Input } from '../../../shared/ui/input';
import { Banner } from '../../../shared/ui/layout';
import { Modal } from '../../../shared/ui/modal';
import { Note } from '../../../shared/ui/note';
import {
  importPreset,
  type TAuthPreset,
  type TAuthPresetSpec,
} from '../../../entities/auth-preset';

export const PresetImport = ({ onImported }: { onImported: (preset: TAuthPreset) => void }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [key, setKey] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const run = useMutation({
    mutationFn: () => {
      const spec = JSON.parse(text) as TAuthPresetSpec;
      return importPreset({ spec, ...(key ? { key } : {}) });
    },
    onSuccess: (preset) => {
      void queryClient.invalidateQueries({ queryKey: ['auth-presets'] });
      setOpen(false);
      setText('');
      setKey('');
      onImported(preset);
    },
  });

  const submit = () => {
    setParseError(null);
    try {
      JSON.parse(text);
    } catch {
      setParseError('That is not JSON. Paste the file exactly as it was exported.');
      return;
    }
    run.mutate();
  };

  const taken = run.error instanceof ApiError && run.error.code.endsWith('key_taken');

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Import
      </Button>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Import a scheme"
        width="lg"
        description="An exported version, as a file from a colleague or from another installation."
        footerNote="No secret travels in it"
        submit={{
          label: 'Import',
          onSubmit: submit,
          disabled: run.isPending,
          waiting: run.isPending,
        }}
      >
        <div className="flex flex-col gap-3">
          <CodeView
            value={text}
            onChange={setText}
            className="max-h-body rounded-control border border-border-raised"
          />
          <div>
            <FieldLabel htmlFor="import-key">Key, if the one in the file is taken</FieldLabel>
            <Input
              id="import-key"
              size="row"
              mono
              placeholder="vendor-portal-2"
              value={key}
              onChange={(event) => setKey(event.target.value)}
            />
          </div>
          {parseError && <Banner>{parseError}</Banner>}
          {run.isError && (
            <Banner detail={run.error instanceof ApiError ? run.error.code : undefined}>
              {taken
                ? 'A scheme with that key already exists here. Give it another one below.'
                : run.error instanceof Error
                  ? run.error.message
                  : 'The import was refused.'}
            </Banner>
          )}
          <Note>
            A scheme says how to check a signature. What to check it with lives on the source and
            does not travel.
          </Note>
        </div>
      </Modal>
    </>
  );
};
